import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function TermsOfService() {
  const lastUpdated = "November 16, 2025";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/95 backdrop-blur-md sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <Logo size="sm" />
            <Button variant="ghost" asChild>
              <Link to="/" className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-6 py-12">
        <article className="max-w-4xl mx-auto prose prose-slate dark:prose-invert">
          <h1 className="text-4xl font-bold text-foreground mb-2">TERMS OF SERVICE — NORTH REPORTS</h1>
          <p className="text-muted-foreground mb-8">Last Updated: {lastUpdated}</p>

          <p className="text-foreground leading-relaxed">
            These Terms of Service ("Terms") govern your access to and use of the NORTH Reports platform ("Services") operated by North Reports LLC, a Kentucky limited liability company. By creating an account or using NORTH in any capacity, you agree to these Terms.
          </p>

          <p className="text-foreground leading-relaxed">
            If you are accepting on behalf of a real estate team or brokerage, you confirm that you have the authority to bind that organization.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">1. Your Account</h2>
          <p className="text-foreground leading-relaxed">
            You must provide accurate information and keep your login credentials secure. You are responsible for activity under your account.
          </p>
          <p className="text-foreground leading-relaxed">
            Team leaders may have the ability to manage or deactivate agent accounts under their organization.
          </p>
          <p className="text-foreground leading-relaxed">
            You must be at least 18 years old to create an account. By registering, you represent that you meet this age requirement.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">2. Use of the Platform</h2>
          <p className="text-foreground leading-relaxed">
            You agree to use NORTH only for lawful, professional purposes related to real estate. You may not:
          </p>
          <ul className="text-foreground space-y-2">
            <li>Reverse-engineer or attempt to access the platform&apos;s source code</li>
            <li>Interfere with system integrity or security</li>
            <li>Misuse AI-generated outputs or represent them as guaranteed market predictions</li>
            <li>Use content in ways that violate someone else&apos;s rights</li>
            <li>Exceed your plan&apos;s usage limits (North Reports per month, storage, AI summaries)</li>
            <li>Share or resell your account access to unauthorized parties</li>
          </ul>
          <p className="text-foreground leading-relaxed">
            We may suspend or terminate accounts that violate these rules.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">3. Subscription Plans & Billing</h2>
          <p className="text-foreground leading-relaxed">
            NORTH offers multiple subscription tiers with different usage limits:
          </p>
          <ul className="text-foreground space-y-2">
            <li><strong>Starter Plan:</strong> Up to 3 active North Reports</li>
            <li><strong>Lite Plan:</strong> Up to 12 active North Reports</li>
            <li><strong>Growth Plan:</strong> Up to 25 active North Reports</li>
            <li><strong>Scale Plan:</strong> Up to 40 active North Reports</li>
          </ul>
          <p className="text-foreground leading-relaxed mt-4">
            If you subscribe to a paid plan:
          </p>
          <ul className="text-foreground space-y-2">
            <li>Subscriptions are billed monthly or annually at the start of each billing period</li>
            <li>All plans include a 14-day free trial</li>
            <li>Annual subscriptions provide one month free (pay for 11 months, get 12)</li>
            <li>Your subscription automatically renews unless canceled before the renewal date</li>
            <li>Fees are charged via Polar, our secure payment processor</li>
            <li>We may adjust pricing with 30 days advance notice</li>
            <li>Access may be suspended for unpaid invoices after a grace period</li>
          </ul>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">4. Upgrades, Downgrades & Proration</h2>
          <p className="text-foreground leading-relaxed">
            <strong>Upgrades:</strong> When you upgrade your plan, you will be charged a prorated amount for the remainder of your current billing period. This means you only pay the difference between your old and new plan for the days remaining. Your new plan limits take effect immediately upon successful payment.
          </p>
          <p className="text-foreground leading-relaxed">
            <strong>Downgrades:</strong> When you downgrade your plan, the change takes effect at the end of your current billing period. You retain access to your current plan features until that date. No refunds are provided for the unused portion of your current plan.
          </p>
          <p className="text-foreground leading-relaxed">
            <strong>Annual to Monthly:</strong> If you switch from annual to monthly billing, the change takes effect at the end of your annual term. No partial refunds are provided.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">5. Refund Policy</h2>
          <p className="text-foreground leading-relaxed">
            Subscription fees are generally non-refundable, except:
          </p>
          <ul className="text-foreground space-y-2">
            <li>Where required by applicable law</li>
            <li>If NORTH is unable to provide the Services due to our fault</li>
            <li>In cases of duplicate charges or billing errors (contact us within 30 days)</li>
          </ul>
          <p className="text-foreground leading-relaxed">
            We do not provide refunds for: partial months of service, unused features, plan downgrades, or cancelations with time remaining on your subscription. If you cancel, you retain access until the end of your paid period.
          </p>
          <p className="text-foreground leading-relaxed">
            For billing disputes or refund requests, contact <a href="mailto:billing@northreports.com" className="text-primary hover:underline">billing@northreports.com</a>.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">6. Cancellation</h2>
          <p className="text-foreground leading-relaxed">
            You may cancel your subscription at any time from your account settings. Upon cancellation:
          </p>
          <ul className="text-foreground space-y-2">
            <li>Your subscription will remain active until the end of your current billing period</li>
            <li>You will not be charged for future periods</li>
            <li>Your subscription will end after your paid period ends</li>
            <li>Data may become inaccessible (but not deleted) until you resubscribe</li>
          </ul>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">7. AI-Generated Content</h2>
          <p className="text-foreground leading-relaxed">
            NORTH uses artificial intelligence to create summaries, insights, and narrative content.
            By using the platform, you acknowledge:
          </p>
          <ul className="text-foreground space-y-2">
            <li>AI outputs may contain inaccuracies</li>
            <li>Outputs should be reviewed before being shared with clients</li>
            <li>NORTH is not responsible for any decisions made solely based on AI output</li>
            <li>AI-generated content does not constitute legal, financial, or professional advice</li>
          </ul>
          <p className="text-foreground leading-relaxed">
            You retain rights to the content you upload or generate within your account.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">8. Social Media Integrations</h2>
          <p className="text-foreground leading-relaxed">
            When you connect Facebook, Instagram, YouTube, or other platforms:
          </p>
          <ul className="text-foreground space-y-2">
            <li>You authorize NORTH to access content and analytics as permitted by the provider&apos;s APIs</li>
            <li>You acknowledge that availability of data may depend on the provider&apos;s permissions and policies</li>
            <li>Disconnecting your account revokes our ability to retrieve new data</li>
            <li>You agree not to misuse or misrepresent social media data displayed in the platform</li>
          </ul>
          <p className="text-foreground leading-relaxed">
            You must comply with Meta, Google, and other third-party API terms.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">9. Data Ownership</h2>
          <p className="text-foreground leading-relaxed font-semibold">You own:</p>
          <ul className="text-foreground space-y-2">
            <li>Your listings</li>
            <li>Your team&apos;s content</li>
            <li>Your social media posts</li>
            <li>Your uploaded material</li>
          </ul>
          <p className="text-foreground leading-relaxed font-semibold mt-4">NORTH owns:</p>
          <ul className="text-foreground space-y-2">
            <li>The platform</li>
            <li>The software</li>
            <li>The AI models and prompts</li>
            <li>The overall design and experience</li>
            <li>Aggregated, anonymized performance data</li>
          </ul>
          <p className="text-foreground leading-relaxed">
            We do not resell user data.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">10. Limitation of Liability</h2>
          <p className="text-foreground leading-relaxed">
            To the fullest extent permitted by law:
          </p>
          <ul className="text-foreground space-y-2">
            <li>NORTH is provided "as-is" without warranties of any kind</li>
            <li>We are not liable for indirect, incidental, special, or consequential damages</li>
            <li>Our total liability will not exceed the amount you paid in the last 12 months</li>
            <li>We are not responsible for lost profits, data loss, or business interruption</li>
          </ul>
          <p className="text-foreground leading-relaxed">
            Real estate markets, platform metrics, and social media analytics are outside our control.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">11. Indemnification</h2>
          <p className="text-foreground leading-relaxed">
            You agree to indemnify and hold harmless North Reports LLC, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorney fees) arising from your use of the Services or violation of these Terms.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">12. Termination</h2>
          <p className="text-foreground leading-relaxed">
            You may cancel your account at any time from your account settings.
            We may suspend or terminate accounts for violations of these Terms or misuse of the platform.
          </p>
          <p className="text-foreground leading-relaxed">
            Upon termination, we may retain certain data only as required for legal or operational reasons. You may request data export before cancellation.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">13. Governing Law & Disputes</h2>
          <p className="text-foreground leading-relaxed">
            These Terms are governed by the laws of the Commonwealth of Kentucky, without regard to conflict of law principles. Any disputes arising from these Terms or your use of the Services shall be resolved in the courts located in Kentucky.
          </p>
          <p className="text-foreground leading-relaxed">
            Before initiating formal legal proceedings, you agree to attempt to resolve disputes informally by contacting us at <a href="mailto:legal@northreports.com" className="text-primary hover:underline">legal@northreports.com</a>.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">14. Changes to These Terms</h2>
          <p className="text-foreground leading-relaxed">
            We may update these Terms from time to time. We will notify you of material changes via email or through the platform at least 30 days before they take effect. Continued use of the platform after such notice means you accept the updated Terms.
          </p>
          <p className="text-foreground leading-relaxed">
            If you do not agree to the updated Terms, you must stop using the Services and cancel your subscription.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">15. Severability</h2>
          <p className="text-foreground leading-relaxed">
            If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that these Terms will otherwise remain in full force and effect.
          </p>

          <hr className="my-8 border-border" />

          <h2 className="text-2xl font-bold text-foreground mt-8 mb-4">16. Contact Us</h2>
          <p className="text-foreground leading-relaxed">
            For questions about these Terms:
          </p>
          <ul className="text-foreground space-y-2">
            <li>Email: <a href="mailto:support@northreports.com" className="text-primary hover:underline">support@northreports.com</a></li>
            <li>Billing inquiries: <a href="mailto:billing@northreports.com" className="text-primary hover:underline">billing@northreports.com</a></li>
            <li>Legal matters: <a href="mailto:legal@northreports.com" className="text-primary hover:underline">legal@northreports.com</a></li>
          </ul>
          <p className="text-foreground leading-relaxed mt-4">
            North Reports LLC<br />
            Kentucky, United States
          </p>
        </article>
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-card mt-12">
        <div className="container mx-auto px-6 py-8">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} North Reports. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
